import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from './ui/tabs';
import { CheckCircle2, XCircle, Eye, Loader2, PenSquare, Trash2, Pin, PinOff } from 'lucide-react';
import { adminBlogsAPI, resolveImageUrl, CATEGORY_LABELS } from '../services/blogsApi';

const StatusPill = ({ status }) => {
  const map = {
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-emerald-100 text-emerald-800',
    rejected: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${map[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const AdminBlogsTab = ({ onCountChange }) => {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewFull, setPreviewFull] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchBlogs = async (status = statusFilter) => {
    try {
      setLoading(true);
      const res = await adminBlogsAPI.list(status);
      setBlogs(res.data || []);
      if (status === 'pending' && onCountChange) onCountChange(res.data?.length || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(statusFilter);
  }, [statusFilter]);

  const openPreview = async (blog) => {
    setPreview(blog);
    setPreviewFull(null);
    try {
      setPreviewLoading(true);
      const res = await adminBlogsAPI.get(blog.id);
      setPreviewFull(res.data);
    } catch (e) {
      toast.error('Could not load blog content');
    } finally {
      setPreviewLoading(false);
    }
  };

  const approve = async (blog) => {
    try {
      setBusyId(blog.id);
      await adminBlogsAPI.approve(blog.id);
      toast.success('Blog approved and published');
      setPreview(null);
      await fetchBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      setBusyId(rejectTarget.id);
      await adminBlogsAPI.reject(rejectTarget.id, rejectReason.trim());
      toast.success('Blog rejected — author notified');
      setRejectTarget(null);
      setRejectReason('');
      setPreview(null);
      await fetchBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  const removeBlog = async (blog) => {
    if (!window.confirm(`Delete blog "${blog.title}"? This cannot be undone.`)) return;
    try {
      setBusyId(blog.id);
      await adminBlogsAPI.remove(blog.id);
      toast.success('Blog deleted');
      await fetchBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    } finally {
      setBusyId(null);
    }
  };

  const toggleFeature = async (blog) => {
    try {
      setBusyId(blog.id);
      if (blog.is_featured) {
        await adminBlogsAPI.unfeature(blog.id);
        toast.success('Blog unpinned from featured slot');
      } else {
        await adminBlogsAPI.feature(blog.id);
        toast.success('Blog pinned as featured — will appear at top of /blog');
      }
      await fetchBlogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update featured');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-blogs-tab">
      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="filter-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="filter-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="filter-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={statusFilter} className="mt-4">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#7CB342] mx-auto" />
            </div>
          ) : blogs.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="empty-admin-blogs">
              <PenSquare className="h-10 w-10 mx-auto text-gray-300 mb-2" />
              <p>No blogs in &quot;{statusFilter}&quot; state.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {blogs.map((b) => (
                <Card key={b.id} data-testid={`admin-blog-${b.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {b.cover_image ? (
                        <img
                          src={resolveImageUrl(b.cover_image)}
                          alt=""
                          className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-lg bg-emerald-50 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-semibold text-sm line-clamp-2">
                            {b.is_featured && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white mr-1.5 align-middle"
                                data-testid={`featured-badge-${b.id}`}
                              >
                                ★ Featured
                              </span>
                            )}
                            {b.title}
                          </h3>
                          <StatusPill status={b.status} />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          By {b.author?.name || '—'}
                          {b.author?.role && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-gray-100 text-[10px]">
                              {b.author.role}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {CATEGORY_LABELS[b.category] || b.category}
                          {b.destination ? ` · ${b.destination}` : ''}
                        </p>
                        {b.status === 'rejected' && b.rejection_reason && (
                          <p className="text-xs text-red-700 mt-1 line-clamp-2">
                            <strong>Reason:</strong> {b.rejection_reason}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPreview(b)}
                            data-testid={`preview-blog-${b.id}`}
                          >
                            <Eye className="h-3 w-3 mr-1" /> Preview
                          </Button>
                          {b.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => approve(b)}
                                disabled={busyId === b.id}
                                className="bg-[#7CB342] hover:bg-[#6da036]"
                                data-testid={`approve-blog-${b.id}`}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { setRejectTarget(b); setRejectReason(''); }}
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`reject-blog-${b.id}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {b.status === 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleFeature(b)}
                              disabled={busyId === b.id}
                              className={b.is_featured
                                ? 'text-orange-600 border-orange-200 hover:bg-orange-50'
                                : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                              }
                              data-testid={`feature-blog-${b.id}`}
                            >
                              {b.is_featured ? (
                                <><PinOff className="h-3 w-3 mr-1" /> Unpin</>
                              ) : (
                                <><Pin className="h-3 w-3 mr-1" /> Pin as Featured</>
                              )}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => removeBlog(b)}
                            disabled={busyId === b.id}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            data-testid={`delete-blog-${b.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="blog-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">{preview?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600 flex items-center gap-3 flex-wrap">
              <span>By {preview?.author?.name}</span>
              <span>·</span>
              <span>{CATEGORY_LABELS[preview?.category] || preview?.category}</span>
              {preview?.destination && (<><span>·</span><span>{preview.destination}</span></>)}
              <StatusPill status={preview?.status} />
            </div>
            {previewFull?.cover_image && (
              <img
                src={resolveImageUrl(previewFull.cover_image)}
                alt=""
                className="w-full rounded-lg aspect-[16/9] object-cover"
              />
            )}
            {previewLoading ? (
              <div className="text-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : (
              <div
                className="blog-body prose max-w-none"
                dangerouslySetInnerHTML={{ __html: previewFull?.body_html || '' }}
              />
            )}
            {previewFull?.tags?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {previewFull.tags.map((t) => (
                  <Badge key={t} variant="outline" className="text-xs">#{t}</Badge>
                ))}
              </div>
            )}
          </div>
          {preview?.status === 'pending' && (
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setRejectTarget(preview); setRejectReason(''); }}
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="preview-reject-btn"
              >
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
              <Button
                onClick={() => approve(preview)}
                disabled={busyId === preview?.id}
                className="bg-[#7CB342] hover:bg-[#6da036]"
                data-testid="preview-approve-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Publish
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent data-testid="blog-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject blog</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              Share why this blog isn&apos;t being approved. The author will see this message
              in-dashboard and can revise & resubmit.
            </p>
            <Label htmlFor="blog-reject-reason" className="text-sm">Reason *</Label>
            <Textarea
              id="blog-reject-reason"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g., Content is too promotional / off-topic / needs more original insights…"
              data-testid="blog-reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              onClick={reject}
              disabled={busyId === rejectTarget?.id}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-reject-blog"
            >
              {busyId === rejectTarget?.id ? 'Rejecting…' : 'Reject blog'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogsTab;
